/* Core & libraries */
import React, { useState, useEffect, Suspense } from "react";
import { useTheme } from "react-jss";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import moment from "helpers/moment";
import { FormattedMessage } from "react-intl";

/* Helpers, constants, custom methods, internal libraries */
import { useSafetyService } from "../../../services/safetyService";
import { lazyWithPreload } from "helpers/lazyLoadingHelpers";
import { useUserContext } from "contexts/userContext";
import { AIRLINES } from "constants/appConstants";

/* Components */
import SafetyFilters from "../../../components/safety/safetyFilters";
import SafetyGrid from "../../../components/safety/grid";
import InviteGuestModal from "../../../components/modals/inviteGuestModal";
import TextLoadingSpinner from "../../../components/spinners/textLoadingSpinner";
import SpinnerBasic from "components/spinners/SpinnerBasic";

// Note: ucmap is lazy-loaded to keep mapbox-gl out of the main bundle and reduce main bundle/initial load size
export const UCMapLazy = lazyWithPreload(() => import('../../../components/ucmap'));

/* Styles */
import styles from "./styles.js"

const SafetyBase = ({
  titleId,
  subtitleId,
  displayBookingInfoModal,
  detailsModal,
}) => {
  const theme = useTheme();
  const classes = styles({ theme });

  const userDetails = useUserContext();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;

  const [gridOptions, setGridOptions] = useState({
    page: 1,
    startDate: moment().startOf("month").format("YYYY-MM-DD"),
    endDate: moment().endOf("month").format("YYYY-MM-DD"),
    type: "month",
  });

  const {
    safetyResponse,
    mapResponse,
    sendGuestInvite,
    getSafetyGridData,
    getFlightDetails
  } = useSafetyService(gridOptions);

  const [showInviteGuestModal, setShowInviteGuestModal] = useState(false);
  const [travelerName, setTravelerName] = useState(null);
  const [showSkeleton, setShowSkeleton] = useState(true);

  const refreshGridData = () => {
    setShowSkeleton(false);
    getSafetyGridData(gridOptions);
  };

  useEffect(() => {
    const interval = setInterval(refreshGridData, 300000);
    setShowSkeleton(true);
    return () => clearInterval(interval);
  }, [gridOptions]);

  const displayInviteGuestModal = (e, name) => {
    e.preventDefault();
    e.stopPropagation();
    setTravelerName(name);
    setShowInviteGuestModal(true);
  };

  return (
    <div className={classes.wrapper}>
      <Container className={classes.container}>
        <Row>
          <Col xs={12} className="gx-0">
            <h1 className={classes.title}>
              <FormattedMessage id={titleId} />
            </h1>
            <p className={classes.subtitle}>
              <FormattedMessage id={subtitleId} />
            </p>
          </Col>
        </Row>
      </Container>

      <Suspense fallback={<SpinnerBasic />}>
        {Object.keys(mapResponse).length === 0 ? (
          <TextLoadingSpinner loadingText={false} />
        ) : (
          <>
            <div className={classes.map}>
              <UCMapLazy airports={mapResponse?.locations} />
            </div>

            <SafetyFilters gridOptions={gridOptions} setGridOptions={setGridOptions} />

            <SafetyGrid
              safetyResponse={safetyResponse}
              gridOptions={gridOptions}
              setGridOptions={setGridOptions}
              showSkeleton={showSkeleton}
              displayInviteGuestModal={displayInviteGuestModal}
              displayBookingInfoModal={(row) =>
                displayBookingInfoModal(row, {
                  getFlightDetails,
                  refreshGridData,
                })
              }
            />

            {detailsModal}

            <InviteGuestModal
              showModal={showInviteGuestModal}
              setShowModal={setShowInviteGuestModal}
              travelerName={travelerName}
              inviteGuest={sendGuestInvite}
            />
          </>
        )}
      </Suspense>
    </div>
  );
};

export default SafetyBase;